import {
  AddIcon,
  Button,
  Card,
  ExternalIcon,
  Flex,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import type { ReactNode } from 'react';
import { useState } from 'react';

import type {
  ConditionApplyToOption,
  ConditionSelectionType,
  ConditionSet,
  ConditionValue,
} from './conditions.types';
import { ConditionsGroup } from './conditionsGroup';
import { ConfirmDeleteConditions } from './confirmDeleteConditions';

import styles from './conditionsEditor.module.css';

const MAX_GROUPS = 7;

const isGroupEmpty = (set: ConditionSet) =>
  Object.keys(set.selections.includes).length === 0 &&
  Object.keys(set.selections.excludes).length === 0;

export interface ConditionsEditorProps {
  /** The "Apply to" choices, each carrying the sections it renders */
  applyToOptions: ConditionApplyToOption[];
  /** String to complete the header verbiage "Conditions for..." */
  conditionFor: string;
  conditionSets: ConditionSet[];
  /** Mount groups collapsed, as when editing a saved special; groups the user adds still open expanded */
  defaultCollapsed?: boolean;
  /** Disable all fields */
  disabled?: boolean;
  /** Required if there are multiple conditions editor components on one page, prevents field name collision */
  editorIndex?: number;
  /** Optional fields to show in the header of the editor component */
  headerFields?: ReactNode;
  /** Hide all conditions groups and the button to add more */
  hideConditions?: boolean;
  /** Callback when a conditions group is added */
  onAddGroup?: () => void;
  /** Callback when "Apply To" radio value is changed */
  onApplyToChange: (index: number, value: string) => void;
  /** Callback when conditions are changed for a particular condition group */
  onChangeGroup: (
    index: number,
    selectionType: ConditionSelectionType,
    key: string,
    value: ConditionValue
  ) => void;
  /** Callback when a conditions group is deleted */
  onDeleteGroup: (index: number) => void;
  /** Callback when conditions are cleared for a particular condition group */
  onDeselectGroup: (index: number, key: string) => void;
  /** Callback when a conditions group is duplicated */
  onDuplicateGroup: (index: number) => void;
  /** Ability to add multiple conditions groups, or restricted to only one */
  variant?: 'multiple' | 'single';
}

export const ConditionsEditor = ({
  applyToOptions,
  conditionFor,
  conditionSets,
  defaultCollapsed = false,
  disabled,
  editorIndex = 0,
  headerFields,
  hideConditions,
  onAddGroup,
  onChangeGroup,
  onDeleteGroup,
  onDeselectGroup,
  onDuplicateGroup,
  onApplyToChange,
  variant = 'multiple',
}: ConditionsEditorProps) => {
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>();
  const [expandedByIndex, setExpandedByIndex] = useState<
    (boolean | undefined)[]
  >([]);
  const toast = useToast();

  /** A set whose `applyTo` matches no option has no sections to render */
  const conditionsFor = (applyTo: string) =>
    applyToOptions.find((option) => option.value === applyTo)?.conditions ?? [];

  const renderMultipleConditionsHeader = () => (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex flexDirection="column">
        <Typography variant="h2-semibold">
          Conditions for {conditionFor}
        </Typography>
        <Typography>
          Products qualify if they match ANY group of conditions
        </Typography>
      </Flex>
      <Button
        variant="tertiary"
        endIcon={<ExternalIcon />}
        label="See how this works"
        href="https://docs.iheartjane.com/partner-success/deals-and-specials/jane-specials/advanced-specials-logic-multiple-condition-groups"
      />
    </Flex>
  );

  const spliceExpanded = (
    start: number,
    deleteCount: number,
    ...inserted: (boolean | undefined)[]
  ) =>
    setExpandedByIndex((current) => {
      const next = [...current];
      next.splice(start, deleteCount, ...inserted);
      return next;
    });

  const deleteGroup = (index: number) => {
    spliceExpanded(index, 1);
    onDeleteGroup(index);
    toast.add({
      label: 'Condition group successfully deleted',
      variant: 'success',
    });
  };

  return (
    <>
      <Card.Group width="100%" direction="column">
        {(variant === 'multiple' || headerFields) && (
          <Card.Content p={40}>
            <>
              {variant === 'multiple' && renderMultipleConditionsHeader()}
              {headerFields}
            </>
          </Card.Content>
        )}
        {!hideConditions && (
          <>
            {[...conditionSets].map((set, index) => (
              <Card.Content
                className={styles['cardContent']}
                key={`conditions-group-${editorIndex}-${index}`}
                px={40}
                py={variant === 'multiple' ? 24 : 40}
                border={variant === 'multiple' ? 'grays-light' : undefined}
                borderWidth="1px 0 0 0"
              >
                <ConditionsGroup
                  applyTo={set.applyTo}
                  applyToOptions={applyToOptions}
                  conditions={conditionsFor(set.applyTo)}
                  disabled={disabled}
                  expanded={expandedByIndex[index] ?? !defaultCollapsed}
                  isGrouped={variant === 'multiple'}
                  id={`condition-group-${editorIndex}-${index}`}
                  onApplyToChange={(value: string) =>
                    onApplyToChange(index, value)
                  }
                  onChange={(
                    selectionType: ConditionSelectionType,
                    key: string,
                    value: ConditionValue
                  ) => onChangeGroup(index, selectionType, key, value)}
                  onDelete={() => {
                    if (isGroupEmpty(set)) {
                      deleteGroup(index);
                    } else {
                      setDeleteIndex(index);
                    }
                  }}
                  onDeselect={(key: string) => onDeselectGroup(index, key)}
                  onDuplicate={
                    conditionSets.length < MAX_GROUPS
                      ? () => {
                          setExpandedByIndex(
                            Array.from(
                              { length: conditionSets.length + 1 },
                              (_, i) => i === index + 1
                            )
                          );
                          onDuplicateGroup(index);
                        }
                      : undefined
                  }
                  onExpandedChange={(expanded: boolean) =>
                    setExpandedByIndex((current) => {
                      const next = [...current];
                      next[index] = expanded;
                      return next;
                    })
                  }
                  overlaps={set.overlaps}
                  selection={set.selections}
                  title={
                    variant === 'multiple'
                      ? `Condition Group ${index + 1}`
                      : `Conditions for ${conditionFor}`
                  }
                />
                {index < conditionSets.length - 1 && (
                  <Flex width="100%" justifyContent="center">
                    <Typography
                      className={styles['groupSeparator']}
                      color="grays-mid"
                      variant="caps-semibold"
                    >
                      Or
                    </Typography>
                  </Flex>
                )}
              </Card.Content>
            ))}
            {variant === 'multiple' && (
              <Card.Content
                className={styles['centeredFlex']}
                px={40}
                py={24}
                border={variant === 'multiple' ? 'grays-light' : 'none'}
                borderWidth="1px 0 0 0"
              >
                <Button
                  disabled={
                    disabled ||
                    conditionSets.length >= MAX_GROUPS ||
                    conditionSets.some(isGroupEmpty)
                  }
                  onClick={() => {
                    setExpandedByIndex([
                      ...conditionSets.map(() => false),
                      true,
                    ]);
                    onAddGroup?.();
                  }}
                  variant="secondary"
                  startIcon={<AddIcon size="sm" />}
                  label="New condition group"
                />
                {!disabled && conditionSets.length >= MAX_GROUPS && (
                  <Typography color="grays-mid" mt={16}>
                    You can have up to 7 groups. Remove one to add another.
                  </Typography>
                )}
              </Card.Content>
            )}
          </>
        )}
      </Card.Group>
      {deleteIndex !== undefined && (
        <ConfirmDeleteConditions
          conditions={conditionsFor(conditionSets[deleteIndex].applyTo)}
          groupNumber={deleteIndex + 1}
          onCancel={() => setDeleteIndex(undefined)}
          onConfirm={() => {
            setDeleteIndex(undefined);
            deleteGroup(deleteIndex);
          }}
          selection={conditionSets[deleteIndex].selections}
        />
      )}
    </>
  );
};
